/**
 * ShazamRecognitionHealthIndicator — 2026-07-18
 * ------------------------------------------------------------------
 * Read-only 24h Shazam Recognition Health summary. Sits next to the
 * existing ShazamPipelineIndicator with the same visual style.
 *
 * Collapsed  : 🟢/🟡/🔴/⚫ totals + %
 * Expanded   : sortable per-station table with drill-down
 * Drill-down : click 🟢/🟡/🔴/⚫ number → recent recognition events
 *
 * Data sources (backend):
 *   GET /api/admin/diagnostic/shazam-recognition-health
 *   GET /api/admin/diagnostic/shazam-recognition-health/{sid}/events
 *
 * Zero polling on the recognition pipeline; pure aggregate reads.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ChevronDown, ChevronUp, Music2, X } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const DOT_CLASSES = {
  green:  'bg-emerald-500',
  yellow: 'bg-amber-500',
  red:    'bg-red-500',
  black:  'bg-gray-700 dark:bg-gray-500',
};

// Recording Summary — v2 header shown ABOVE drill-down events
const RecordingSummary = ({ station }) => {
  const win = station.recording_window || {};
  const missing = station.missing_analysis || {};
  const explained = missing.explained_missing || {};
  const base = station.baseline || {};
  return (
    <div
      data-testid={`reco-health-summary-${station.station_id}`}
      className="border-t border-indigo-400/30 bg-indigo-50/40 dark:bg-indigo-950/20 px-3 py-2 text-[11px] grid grid-cols-1 md:grid-cols-3 gap-3"
    >
      <div>
        <div className="text-[10px] font-semibold uppercase text-indigo-600 dark:text-indigo-400 mb-1">
          Recording Summary · Today
        </div>
        <div className="space-y-0.5">
          <div className="flex justify-between"><span className="text-gray-500">Window</span><span className="font-mono">{win.start}→{win.end}</span></div>
          <div className="flex justify-between"><span className="text-gray-500">Elapsed</span><span className="font-mono">{win.elapsed_str} / {win.duration_str} ({win.progress_pct}%)</span></div>
          <div className="flex justify-between"><span className="text-gray-500">Expected</span><span className="font-bold">{station.expected}</span></div>
          <div className="flex justify-between"><span className="text-gray-500">Detected</span><span className="font-bold text-emerald-600">{station.detected}</span></div>
          <div className="flex justify-between"><span className="text-gray-500">Opportunities</span><span className="font-mono">{station.opportunities}</span></div>
          <div className="flex justify-between"><span className="text-gray-500">Gap</span><span className="font-bold text-red-600">{station.gap > 0 ? `-${station.gap}` : '0'}</span></div>
          <div className="flex justify-between"><span className="text-gray-500">Completeness</span><span className="font-bold">{station.completeness_pct != null ? `${station.completeness_pct}%` : '—'}</span></div>
        </div>
      </div>
      <div>
        <div className="text-[10px] font-semibold uppercase text-indigo-600 dark:text-indigo-400 mb-1">
          Baseline & Sampling
        </div>
        <div className="space-y-0.5">
          <div className="flex justify-between"><span className="text-gray-500">Strategy</span><span className="font-mono">{station.strategy}</span></div>
          <div className="flex justify-between"><span className="text-gray-500">14d baseline songs/h</span><span className="font-mono">{base.available ? base.baseline_songs_per_hour : '—'}</span></div>
          <div className="flex justify-between"><span className="text-gray-500">Healthy days</span><span className="font-mono">{base.available ? `${base.healthy_days}/${base.sample_days}` : '—'}</span></div>
          <div className="flex justify-between"><span className="text-gray-500">Sampling interval</span><span className="font-mono">{station.sampling?.sampling_interval_s}s</span></div>
          <div className="flex justify-between"><span className="text-gray-500">Avg samples/song</span><span className="font-mono">{station.sampling?.avg_samples_per_song ?? '—'}</span></div>
        </div>
      </div>
      <div>
        <div className="text-[10px] font-semibold uppercase text-indigo-600 dark:text-indigo-400 mb-1">
          Missing Analysis · Gap {missing.gap_total || 0}
        </div>
        <div className="space-y-0.5">
          <div className="text-[10px] font-semibold text-gray-600 dark:text-gray-400">
            Explained ({missing.explained_missing_total || 0})
          </div>
          {Object.entries(explained).filter(([, n]) => n > 0).map(([k, n]) => (
            <div key={k} className="flex justify-between text-[10px]">
              <span className="text-gray-500">{k}</span><span className="font-mono">{n}</span>
            </div>
          ))}
          {missing.explained_missing_total === 0 && (
            <div className="text-[9px] text-gray-400 italic">no known losses</div>
          )}
          <div className="text-[10px] font-semibold text-gray-600 dark:text-gray-400 pt-1 border-t border-gray-200 dark:border-gray-800 mt-1">
            Unexplained · Unknown
          </div>
          <div className="flex justify-between text-[10px]">
            <span className="text-gray-500">unknown</span>
            <span className="font-mono font-bold text-amber-600">
              {missing.unexplained_missing_unknown || 0}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

const DOT_LABEL = {
  green:  '🟢',
  yellow: '🟡',
  red:    '🔴',
  black:  '⚫',
};

const SORT_KEYS = [
  { key: 'success_pct',    label: 'Success %' },
  { key: 'green',          label: '🟢' },
  { key: 'yellow',         label: '🟡' },
  { key: 'red',            label: '🔴' },
  { key: 'black',          label: '⚫' },
  { key: 'last_success',   label: 'Last Success' },
  { key: 'station_name',   label: 'Station' },
];

const fmt = (v) => (v == null || v === '' ? '—' : String(v));

const fmtTime = (iso) => {
  if (!iso || iso === 'not_available') return '—';
  try {
    const d = new Date(iso);
    return `${d.getHours().toString().padStart(2, '0')}:${d
      .getMinutes()
      .toString()
      .padStart(2, '0')}`;
  } catch {
    return iso;
  }
};

const CountPill = ({ tone, n, onClick, testid }) => (
  <button
    type="button"
    data-testid={testid}
    onClick={onClick}
    disabled={!onClick}
    className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold tabular-nums ${
      onClick ? 'hover:ring-1 hover:ring-indigo-400/60 cursor-pointer' : 'cursor-default'
    }`}
    title={`${tone} events`}
  >
    <span className={`inline-block w-2 h-2 rounded-full ${DOT_CLASSES[tone]}`} />
    <span>{n ?? 0}</span>
  </button>
);

// Drill-down modal — inline expansion inside the panel
const EventsDrillDown = ({ station, onClose, token, initialFilter }) => {
  const [events, setEvents] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState(initialFilter || 'all');

  useEffect(() => {
    setFilter(initialFilter || 'all');
  }, [initialFilter, station.station_id]);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const res = await fetch(
          `${API}/api/admin/diagnostic/shazam-recognition-health/${station.station_id}/events?limit=200`,
          { headers }
        );
        if (res.ok && !cancel) {
          const j = await res.json();
          setEvents(j.events || []);
        }
      } catch {
        // silent
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => {
      cancel = true;
    };
  }, [station.station_id, token]);

  // Filter events by outcome tone
  const filteredEvents = useMemo(() => {
    if (!events) return [];
    if (filter === 'all') return events;
    const RESULT_TYPE_BY_TONE = {
      green:  new Set(['HIT']),
      yellow: new Set(['NOMATCH', 'CACHE_HIT', 'SKIPPED']),
      red:    new Set(['TIMEOUT', 'EXCEPTION']),
    };
    const YELLOW_ERROR_TYPES = new Set(['pacer_deferred']);
    return events.filter((e) => {
      const rt = e.result_type;
      if (filter === 'yellow' && rt === 'EXCEPTION'
          && YELLOW_ERROR_TYPES.has((e.error_type || '').toLowerCase())) {
        return true; // pacer_deferred belongs to yellow, not red
      }
      if (filter === 'red' && rt === 'EXCEPTION'
          && YELLOW_ERROR_TYPES.has((e.error_type || '').toLowerCase())) {
        return false; // exclude pacer_deferred from red
      }
      return (RESULT_TYPE_BY_TONE[filter] || new Set()).has(rt);
    });
  }, [events, filter]);

  const filterLabel = {
    all:    'All events',
    green:  '🟢 Successful (HIT)',
    yellow: '🟡 Degraded (NOMATCH · CACHE_HIT · SKIPPED · DEFERRED)',
    red:    '🔴 Failed (TIMEOUT · EXCEPTION)',
  }[filter];

  return (
    <div
      data-testid={`reco-health-drilldown-${station.station_id}`}
      className="border-t border-indigo-400/30 bg-gray-50 dark:bg-gray-900/40 p-2 text-[11px]"
    >
      <div className="flex items-center justify-between mb-1.5 flex-wrap gap-2">
        <div className="font-semibold text-gray-700 dark:text-gray-300">
          {station.station_name} · Recent recognition events
          <span className="ml-2 text-[10px] font-normal text-gray-500">
            ({filteredEvents.length} of {events?.length || 0})
          </span>
        </div>
        <div className="flex items-center gap-1">
          {['all', 'green', 'yellow', 'red'].map((f) => (
            <button
              key={f}
              data-testid={`reco-health-filter-${f}-${station.station_id}`}
              onClick={(e) => { e.stopPropagation(); setFilter(f); }}
              className={`px-1.5 py-0.5 rounded text-[9px] font-semibold border ${
                filter === f
                  ? 'bg-indigo-500/20 border-indigo-400/50 text-indigo-700 dark:text-indigo-300'
                  : 'bg-transparent border-gray-300 dark:border-gray-700 text-gray-500 hover:border-indigo-400/40'
              }`}
            >
              {f === 'all' ? 'All' : DOT_LABEL[f]}
            </button>
          ))}
          <button
            data-testid="reco-health-drilldown-close"
            onClick={(e) => { e.stopPropagation(); onClose(); }}
            className="ml-1 text-gray-500 hover:text-gray-800 dark:hover:text-gray-200"
          >
            <X className="w-3 h-3" />
          </button>
        </div>
      </div>
      {filter !== 'all' && (
        <div className="mb-1 text-[10px] text-gray-500 italic">
          Filter: {filterLabel}
        </div>
      )}
      {loading && <div className="text-gray-500">Loading…</div>}
      {!loading && filteredEvents.length === 0 && (
        <div className="text-gray-500 italic">
          {filter === 'all'
            ? 'No telemetry events available for this station (in-memory ring buffer empty).'
            : `No events matching "${filterLabel}" in the telemetry ring buffer.`}
        </div>
      )}
      {!loading && filteredEvents.length > 0 && (
        <div className="max-h-64 overflow-y-auto space-y-0.5">
          {filteredEvents.map((e, i) => (
            <div
              key={i}
              data-testid={`reco-health-event-${i}`}
              className="grid grid-cols-[70px_60px_1fr_auto] gap-2 items-center text-[10px] border-b border-gray-200 dark:border-gray-800 last:border-0 py-0.5"
            >
              <span className="font-mono text-gray-500">
                {fmtTime(e.timestamp)}
              </span>
              <span
                className={`font-mono ${
                  e.result_type === 'HIT'
                    ? 'text-emerald-600'
                    : e.result_type === 'TIMEOUT' || e.result_type === 'EXCEPTION'
                    ? 'text-red-600'
                    : 'text-amber-600'
                }`}
              >
                {e.result_type || '—'}
              </span>
              <span className="truncate">
                {e.artist || e.title ? `${e.artist || '?'} — ${e.title || '?'}` : (
                  <span className="text-gray-400 italic">no track</span>
                )}
              </span>
              <span className="font-mono text-gray-500">
                {e.error_type || `${e.total_ms || 0}ms`}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export const ShazamRecognitionHealthIndicator = ({ token, className = '' }) => {
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(true);
  const [sortKey, setSortKey] = useState('gap');
  const [sortDir, setSortDir] = useState('desc');
  const [drillStationId, setDrillStationId] = useState(null);
  const [drillFilter, setDrillFilter] = useState('all');

  const fetchData = useCallback(async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await fetch(
        `${API}/api/admin/diagnostic/shazam-recognition-health`,
        { headers }
      );
      if (res.ok) {
        const j = await res.json();
        setData(j);
      }
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
    const id = setInterval(fetchData, 60000);
    return () => clearInterval(id);
  }, [fetchData]);

  const sortedStations = useMemo(() => {
    if (!data?.stations) return [];
    const rows = [...data.stations];
    rows.sort((a, b) => {
      const va = a[sortKey] ?? -Infinity;
      const vb = b[sortKey] ?? -Infinity;
      if (typeof va === 'string' && typeof vb === 'string') {
        return sortDir === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
      }
      return sortDir === 'asc' ? va - vb : vb - va;
    });
    return rows;
  }, [data, sortKey, sortDir]);

  if (loading || !data) return null;

  const totals = data.fleet_summary || {
    expected: 0, detected: 0, gap: 0, completeness_pct: null };

  const tooltip =
    `Recording Completeness · ` +
    `Expected ${totals.expected} · Detected ${totals.detected} · ` +
    `Gap ${totals.gap} · ${totals.completeness_pct ?? '—'}%`;

  const drillStation = drillStationId
    ? sortedStations.find((s) => s.station_id === drillStationId)
    : null;

  return (
    <div
      data-testid="shazam-recognition-health-indicator"
      className={`inline-block ${className}`}
    >
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        title={tooltip}
        data-testid="reco-health-toggle"
        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-gradient-to-r from-indigo-500/10 to-emerald-500/10 border border-indigo-400/30 hover:border-indigo-400/60 text-indigo-700 dark:text-indigo-300 text-xs font-medium transition-colors"
      >
        <Music2 className="w-3.5 h-3.5" />
        <span className="font-semibold">Recording Completeness</span>
        <span className="opacity-90 flex items-center gap-2">
          <span className="tabular-nums">
            <span className="text-emerald-600 font-bold">{totals.detected.toLocaleString()}</span>
            <span className="text-gray-400 mx-0.5">/</span>
            <span>{totals.expected.toLocaleString()}</span>
          </span>
          {totals.completeness_pct != null && (
            <span
              className={`font-bold ${
                totals.completeness_pct >= 90
                  ? 'text-emerald-600'
                  : totals.completeness_pct >= 60
                  ? 'text-amber-600'
                  : 'text-red-600'
              }`}
            >
              {totals.completeness_pct}%
            </span>
          )}
          {totals.gap > 0 && (
            <span className="text-red-600 text-[10px]">
              gap -{totals.gap.toLocaleString()}
            </span>
          )}
        </span>
        <span className="ml-1 text-[10px] text-gray-500 hidden sm:inline">
          Today · per-station window
        </span>
        {expanded ? (
          <ChevronUp className="w-3 h-3" />
        ) : (
          <ChevronDown className="w-3 h-3" />
        )}
      </button>

      {expanded && (
        <div
          data-testid="reco-health-expanded"
          className="mt-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-xl text-xs overflow-hidden"
          style={{ minWidth: '760px', maxWidth: '1200px' }}
        >
          <div className="px-3 py-1.5 flex items-center justify-between bg-gray-50 dark:bg-gray-800/60 border-b border-gray-200 dark:border-gray-700">
            <div className="font-semibold text-gray-700 dark:text-gray-300">
              Recording Completeness · {data.station_count} stations
            </div>
            <div className="text-[10px] text-gray-500">
              Today · Counters reset at each station&apos;s schedule_start
            </div>
          </div>

          <div className="overflow-x-auto max-h-[520px] overflow-y-auto">
            <table className="w-full text-[11px]">
              <thead className="bg-gray-50 dark:bg-gray-800/40 sticky top-0">
                <tr className="text-left border-b border-gray-200 dark:border-gray-700">
                  {[
                    { k: 'station_name', label: 'Station' },
                    { k: 'strategy', label: 'Strategy' },
                    { k: 'window', label: 'Window · Progress' },
                    { k: 'expected', label: 'Expected' },
                    { k: 'detected', label: 'Detected' },
                    { k: 'opportunities', label: 'Opportunities' },
                    { k: 'gap', label: 'Gap' },
                    { k: 'completeness_pct', label: 'Completeness' },
                    { k: 'baseline', label: 'Baseline songs/h' },
                    { k: 'last_success', label: 'Last Detection' },
                  ].map(({ k, label }) => {
                    const isSortable = ['expected','detected','gap',
                      'completeness_pct','last_success','station_name',
                      'strategy'].includes(k);
                    return (
                      <th
                        key={k}
                        data-testid={`reco-health-col-${k}`}
                        onClick={() => {
                          if (!isSortable) return;
                          if (sortKey === k) {
                            setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
                          } else {
                            setSortKey(k);
                            setSortDir(k === 'gap' ? 'desc' : 'desc');
                          }
                        }}
                        className={`py-1 px-2 font-semibold text-gray-600 dark:text-gray-300 ${
                          isSortable ? 'cursor-pointer hover:text-indigo-600' : ''
                        }`}
                      >
                        {label}
                        {isSortable && sortKey === k && (
                          <span className="ml-1">{sortDir === 'asc' ? '↑' : '↓'}</span>
                        )}
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {sortedStations.length === 0 && (
                  <tr>
                    <td colSpan={10} className="py-3 text-center text-gray-500 italic">
                      No Shazam-active stations in the current window.
                    </td>
                  </tr>
                )}
                {sortedStations.map((s) => {
                  const isDrilled = drillStationId === s.station_id;
                  const win = s.recording_window || {};
                  const base = s.baseline || {};
                  const strategyBadge = s.strategy === 'SHAZAM_ONLY'
                    ? 'bg-blue-500/15 text-blue-700 dark:text-blue-300 border-blue-400/40'
                    : s.strategy === 'SHAZAM_FALLBACK'
                    ? 'bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-400/40'
                    : 'bg-gray-500/15 text-gray-600 dark:text-gray-400 border-gray-400/40';
                  return (
                    <React.Fragment key={s.station_id}>
                      <tr
                        data-testid={`reco-health-row-${s.station_id}`}
                        className={`border-b border-gray-100 dark:border-gray-800 last:border-0 hover:bg-gray-50 dark:hover:bg-gray-800/50 cursor-pointer ${
                          isDrilled ? 'bg-indigo-50 dark:bg-indigo-950/30' : ''
                        }`}
                        onClick={() => {
                          setDrillStationId(isDrilled ? null : s.station_id);
                          setDrillFilter('all');
                        }}
                      >
                        <td className="py-1 px-2 font-medium truncate max-w-[180px]">
                          {s.station_name || '—'}
                        </td>
                        <td className="py-1 px-2">
                          <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold border ${strategyBadge}`}>
                            {s.strategy === 'SHAZAM_ONLY' ? '🟦 SHAZAM ONLY' :
                             s.strategy === 'SHAZAM_FALLBACK' ? '🟨 FALLBACK' :
                             s.strategy}
                          </span>
                        </td>
                        <td className="py-1 px-2 font-mono text-[10px] text-gray-500">
                          <div>{win.start}→{win.end}</div>
                          <div className="text-[9px]">
                            {win.elapsed_str} / {win.duration_str}
                            {win.progress_pct != null && (
                              <span className="ml-1 text-indigo-500">({win.progress_pct}%)</span>
                            )}
                          </div>
                        </td>
                        <td className="py-1 px-2 tabular-nums font-semibold">{s.expected}</td>
                        <td className="py-1 px-2 tabular-nums font-semibold text-emerald-600">{s.detected}</td>
                        <td className="py-1 px-2 tabular-nums text-gray-500">{s.opportunities}</td>
                        <td className={`py-1 px-2 tabular-nums font-bold ${s.gap > 0 ? 'text-red-600' : 'text-gray-400'}`}>
                          {s.gap > 0 ? `-${s.gap}` : '0'}
                        </td>
                        <td className="py-1 px-2 tabular-nums font-bold">
                          {s.completeness_pct != null ? (
                            <span className={
                              s.completeness_pct >= 90 ? 'text-emerald-600' :
                              s.completeness_pct >= 60 ? 'text-amber-600' :
                              'text-red-600'
                            }>{s.completeness_pct}%</span>
                          ) : <span className="text-gray-400">—</span>}
                        </td>
                        <td className="py-1 px-2 font-mono text-[10px] text-gray-500">
                          {base.available ? (
                            <>
                              {base.baseline_songs_per_hour}
                              <span className="text-[9px] block">
                                {base.healthy_days}/{base.sample_days}d
                              </span>
                            </>
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                        </td>
                        <td className="py-1 px-2 font-mono text-[10px]">
                          {fmtTime(s.last_success)}
                        </td>
                      </tr>
                      {isDrilled && (
                        <tr>
                          <td colSpan={10} className="p-0">
                            <RecordingSummary station={s} />
                            <EventsDrillDown
                              station={s}
                              onClose={() => {
                                setDrillStationId(null);
                                setDrillFilter('all');
                              }}
                              token={token}
                              initialFilter={drillFilter}
                            />
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          {data.data_sources && (
            <div className="px-3 py-1.5 border-t border-gray-200 dark:border-gray-700 text-[9px] text-gray-500">
              <span className="font-semibold">Sources · </span>
              Detected: {data.data_sources.detected_from} ·
              Expected: {data.data_sources.expected_from}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ShazamRecognitionHealthIndicator;
